import dayjs from 'dayjs';

export interface PortfolioFluxSourcePoint {
    readonly date: string;
    readonly activePassiveEuro: number;
    readonly activePassiveLei: number;
}

export interface PortfolioFluxPoint {
    readonly date: string;
    readonly current: number | null;
    readonly thirtyDaysEarlier: number | null;
    readonly previousYear: number | null;
}

function valueForCurrency(point: PortfolioFluxSourcePoint, currency: 'EUR' | 'LEI'): number {
    return currency === 'EUR' ? point.activePassiveEuro : point.activePassiveLei;
}

function valueAt(
    valuesByDate: ReadonlyMap<string, number>,
    date: dayjs.Dayjs,
): number | null {
    return valuesByDate.get(date.format('YYYY-MM-DD')) ?? null;
}

/** Aligns daily active+passive portfolio values with the same day 30 days and one year earlier. */
export function buildPortfolioFluxPoints(
    data: readonly PortfolioFluxSourcePoint[],
    startDate: string,
    endDate: string,
    currency: 'EUR' | 'LEI',
): PortfolioFluxPoint[] {
    const valuesByDate = new Map(data.map(point => [point.date, valueForCurrency(point, currency)]));
    const points: PortfolioFluxPoint[] = [];
    let currentDate = dayjs(startDate);
    const finalDate = dayjs(endDate);

    while (currentDate.isBefore(finalDate) || currentDate.isSame(finalDate, 'day')) {
        points.push({
            date: currentDate.format('YYYY-MM-DD'),
            current: valueAt(valuesByDate, currentDate),
            thirtyDaysEarlier: valueAt(valuesByDate, currentDate.subtract(30, 'day')),
            previousYear: valueAt(valuesByDate, currentDate.subtract(1, 'year')),
        });
        currentDate = currentDate.add(1, 'day');
    }

    return points;
}
